'use client';

import { useState } from 'react';
import { AlertTriangle, AlertCircle, Info, X, ChevronDown, ChevronUp, Check } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/shared/ui/button';
import type { DashboardAlert } from '@/lib/integrations/supabase/types';

interface AlertsPanelProps {
  alerts: DashboardAlert[];
  onAlertClick?: (alert: DashboardAlert) => void;
  onDismiss?: (alert: DashboardAlert) => void;
}

const alertConfig = {
  critical: {
    icon: AlertTriangle,
    bg: 'bg-rose-500/5',
    border: 'border-rose-500/20',
    text: 'text-rose-700 dark:text-rose-400',
    iconColor: 'text-rose-600 dark:text-rose-400',
    badge: 'bg-rose-500/10 text-rose-700 dark:text-rose-400',
    label: 'Requires immediate attention',
  },
  warning: {
    icon: AlertCircle,
    bg: 'bg-amber-500/5',
    border: 'border-amber-500/20',
    text: 'text-amber-700 dark:text-amber-400',
    iconColor: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-500/10 text-amber-700 dark:text-amber-400',
    label: 'Should be reviewed soon',
  },
  info: {
    icon: Info,
    bg: 'bg-blue-500/5',
    border: 'border-blue-500/20',
    text: 'text-blue-700 dark:text-blue-400',
    iconColor: 'text-blue-600 dark:text-blue-400',
    badge: 'bg-blue-500/10 text-blue-700 dark:text-blue-400',
    label: 'For your information',
  },
};

export function AlertsPanel({ alerts, onAlertClick, onDismiss }: AlertsPanelProps) {
  const [dismissedAlerts, setDismissedAlerts] = useState<Set<string>>(new Set());
  const [isCollapsed, setIsCollapsed] = useState(false);

  const visibleAlerts = alerts.filter(
    (alert) => !dismissedAlerts.has(`${alert.metric}-${alert.value}`)
  );

  // Group alerts by severity
  const criticalAlerts = visibleAlerts.filter((a) => a.type === 'critical');
  const warningAlerts = visibleAlerts.filter((a) => a.type === 'warning');
  const infoAlerts = visibleAlerts.filter((a) => a.type === 'info');

  const handleDismiss = (alert: DashboardAlert) => {
    const alertKey = `${alert.metric}-${alert.value}`;
    setDismissedAlerts((prev) => new Set([...prev, alertKey]));
    onDismiss?.(alert);
  };

  if (visibleAlerts.length === 0) {
    return (
      <div className="card-bookdesk !p-0 overflow-hidden">
        <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <AlertTriangle className="h-4 w-4 text-foreground" />
            <h3 className="text-base font-semibold text-foreground">Alerts</h3>
          </div>
        </div>
        <div className="p-8 flex flex-col items-center justify-center text-center">
          <div className="w-12 h-12 rounded-full bg-muted flex items-center justify-center mb-3">
            <Check className="h-6 w-6 text-foreground" />
          </div>
          <p className="text-sm font-medium text-foreground">All Systems Healthy</p>
          <p className="text-xs text-muted-foreground mt-1">No issues detected</p>
        </div>
      </div>
    );
  }

  const renderAlertGroup = (groupAlerts: DashboardAlert[], type: 'critical' | 'warning' | 'info') => {
    if (groupAlerts.length === 0) return null;

    const config = alertConfig[type];
    const Icon = config.icon;
    const label = type.charAt(0).toUpperCase() + type.slice(1);

    return (
      <div className="space-y-2">
        <div className="flex items-center gap-2">
          <Icon className={cn('h-4 w-4', config.iconColor)} />
          <span className={cn('text-xs font-semibold uppercase tracking-wide', config.text)}>
            {label} ({groupAlerts.length})
          </span>
        </div>
        <div className="space-y-1.5">
          {groupAlerts.map((alert, index) => (
            <div
              key={`${alert.metric}-${index}`}
              className={cn(
                'flex items-center justify-between gap-2 p-2 rounded-lg border transition-colors',
                config.bg,
                config.border,
                onAlertClick && 'cursor-pointer hover:opacity-80'
              )}
              onClick={() => onAlertClick?.(alert)}
            >
              <span className={cn('text-sm flex-1', config.text)}>{alert.message}</span>
              <div className="flex items-center gap-2">
                <span className={cn('text-xs font-mono px-1.5 py-0.5 rounded', config.badge)}>
                  {alert.value}
                </span>
                {onDismiss && (
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDismiss(alert);
                    }}
                    className={cn(
                      'p-0.5 rounded hover:bg-black/10 dark:hover:bg-white/10 transition-colors',
                      config.text
                    )}
                    title="Acknowledge"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  return (
    <div className="card-bookdesk !p-0 overflow-hidden">
      {/* Header */}
      <div className="px-4 py-3 border-b border-border bg-muted/30 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <AlertTriangle className="h-4 w-4 text-foreground" />
          <h3 className="text-base font-semibold text-foreground">Alerts</h3>
          <span className="text-sm text-muted-foreground">({visibleAlerts.length})</span>
        </div>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setIsCollapsed(!isCollapsed)}
          className="h-7 w-7 p-0"
        >
          {isCollapsed ? (
            <ChevronDown className="h-4 w-4" />
          ) : (
            <ChevronUp className="h-4 w-4" />
          )}
        </Button>
      </div>

      {/* Content */}
      {!isCollapsed && (
        <div className="p-4 space-y-4">
          {renderAlertGroup(criticalAlerts, 'critical')}
          {renderAlertGroup(warningAlerts, 'warning')}
          {renderAlertGroup(infoAlerts, 'info')}
        </div>
      )}
    </div>
  );
}
